import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState } from '@opu/utils';
import { useHistory, useLocation } from 'react-router-dom';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useTranslation } from 'react-i18next';

const useForm = () => {
  let history = useHistory();
  let location = useLocation();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.params && location.params.id) {
      getBook();
      getAuthors();
      getCategories();
    } else {
      history.push('/book');
    }
  }, []);

  const back = () => {
    history.goBack();
  };

  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const getAuthors = () => {
    setLoading(true);
    AxiosInstance.get('/author')
      .then((res) => {
        setAuthors(res.data.items);
      })
      .catch((error) => {
        Swal.fire(t('author.ERROR_CREATE_BOOK'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createAuthor = (name) => {
    setLoading(true);
    AxiosInstance.post('/author', { name: name })
      .then((res) => {
        Swal.fire(t('author.SUCCESS_CREATE_AUTHOR'), '', 'success');
        getAuthors();
      })
      .catch((error) => {
        Swal.fire(t('author.ERROR_CREATE_BOOK'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const getCategories = () => {
    setLoading(true);
    AxiosInstance.get('/category')
      .then((res) => {
        setCategories(res.data);
      })
      .catch((error) => {
        Swal.fire(t('category.ERROR_CREATE_BOOK'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getBook = () => {
    setLoading(true);
    AxiosInstance.get('/book/' + location.params.id)
      .then((response) => {
        let inputValues = {
          ISBN: response.data.ISBN,
          authors: response.data.authors,
          categories: response.data.categories,
          title: response.data.title,
          cover: response.data.cover,
          pdf: response.data.pdf,
          summary: response.data.summary,
          abstract: response.data.abstract,
          editionNumber: response.data.editionNumber,
          publicationNumber: response.data.publicationNumber,
          publishingHouse: response.data.publishingHouse,
          releaseDate: response.data.releaseDate,
          pages: response.data.pages,
          price: response.data.price || 0.00,
        };
        updateFormState(inputValues);
      })
      .catch((error) => {
        Swal.fire(t('book.ERROR_GET_BOOK'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateBook = () => {
    const formData = new FormData();
    formData.append('ISBN', formState.inputValues.ISBN);
    formState.inputValues.authors.forEach((tag) => {
      if (tag && tag.id) formData.append('authors[]', tag.id);
      else formData.append('authors[]', tag);
    });
    formState.inputValues.categories.forEach((tag) => {
      if (tag && tag.id) formData.append('categories[]', tag.id);
      else formData.append('categories[]', tag);
    });

    if (formState.inputValues.cover instanceof File) {
      formData.append('cover', formState.inputValues.cover);
    }

    if (formState.inputValues.pdf instanceof File) {
      formData.append('pdf', formState.inputValues.pdf);
    }

    formData.append('title', formState.inputValues.title);
    formData.append('summary', formState.inputValues.summary);
    formData.append('abstract', formState.inputValues.abstract);
    formData.append('editionNumber', formState.inputValues.editionNumber);
    formData.append('publicationNumber', formState.inputValues.publicationNumber);
    formData.append('publishingHouse', formState.inputValues.publishingHouse);
    formData.append('releaseDate', formState.inputValues.releaseDate);
    formData.append('pages', formState.inputValues.pages);
    formData.append('price', formState.inputValues.price || 0.00);

    setLoading(true);
    AxiosInstance({
      method: 'patch',
      url: '/book/' + location.params.id,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((response) => {
        Swal.fire(t('book.SUCCESS_UPDATE_BOOK'), '', 'success');
      })
      .catch((error) => {
        if (error.status === 400) {
          handleErrorsServer(error.data.message);
        } else if (error.status === 413) {
          Swal.fire(t('book.ERROR_FILE_TOO_LARGE'), '', 'error');
        } else Swal.fire(t('book.ERROR_UPDATE_BOOK'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const inputValues = {
    ISBN: { value: '', validate: { required: true } },
    authors: { value: '', validate: { required: true } },
    categories: { value: '', validate: { required: true } },
    title: { value: '', validate: { required: true } },
    cover: { value: '', validate: { required: false } },
    pdf: { value: '', validate: { required: false } },
    summary: { value: '', validate: { required: false, max: 10000 } },
    abstract: { value: '', validate: { required: false, max: 10000 } },
    editionNumber: { value: '', validate: { required: true } },
    publicationNumber: { value: '', validate: { required: true } },
    publishingHouse: { value: '', validate: { required: true } },
    releaseDate: { value: '', validate: { required: true } },
    pages: { value: '', validate: { required: true } },
    price: { value: '0.00', validate: { required: false } },
  };

  const { formState, submitHandler, updateFormState, field, handleErrorsServer } = FormState(inputValues, updateBook);

  return {
    submitHandler,
    back,
    loading,
    field,
    authors,
    categories,
    createAuthor,
  };
};

export default useForm;
